import {
	CompactSelect,
	CompactTextInput,
	type CompactSelectOption,
} from "newui/components/ui/InputWithLabel";
import { cn } from "utils/helpers";

import type {
	ColumnConfig,
	QuoteColumnType,
} from "../../SpreadsheetEditor";
import { SAMPLE_CELL_LABELS } from "../../SpreadsheetEditor";
import type { ScopeItemRowDraft } from "../ScopeItemRowsEditor";

const CELL_BASE = "border border-[#EAECF0] px-1 py-1 text-sm";

export interface TemplateRowCellProps {
	column: ColumnConfig;
	row: ScopeItemRowDraft;
	rowIndex: number;
	/** localId of the column bound to row.name (first description column). */
	nameColumnId: string | null;
	/** localId of the column bound to row.unitId (first unit_of_measurement). */
	unitColumnId: string | null;
	unitOptions: CompactSelectOption[];
	/** Force red-border styling on the unit cell (e.g. mandatory-UoM violation). */
	unitHasError?: boolean;
	onChangeRow: (
		rowId: number,
		field: "name" | "unitId",
		value: string | number | null
	) => void;
}

const cellHint = (type: QuoteColumnType): string => {
	if (type === "total") return "Auto-calculated from Response: Value columns";
	if (type === "description")
		return "Only the first Description column is editable in the template";
	return "Supplier will fill this in";
};

/**
 * Renders a single `<td>` in a template row, dispatched on the column type:
 *  - first Description column  -> CompactTextInput bound to row.name
 *  - first Units column        -> CompactSelect bound to row.unitId
 *  - everything else           -> greyed sample-text placeholder
 */
export const TemplateRowCell: React.FC<TemplateRowCellProps> = ({
	column,
	row,
	rowIndex,
	nameColumnId,
	unitColumnId,
	unitOptions,
	unitHasError = false,
	onChangeRow,
}) => {
	if (column.localId === nameColumnId) {
		return (
			<td role="gridcell" className={CELL_BASE}>
				<CompactTextInput
					value={row.name}
					onChange={(e) => onChangeRow(row.id, "name", e.target.value)}
					placeholder="Item name"
					aria-label={`Item name for row ${rowIndex + 1}`}
					data-testid={`template-row-name-${row.id}`}
				/>
			</td>
		);
	}

	if (column.localId === unitColumnId) {
		return (
			<td role="gridcell" className={CELL_BASE} style={{ minWidth: 160 }}>
				<CompactSelect
					value={row.unitId ?? ""}
					hasError={unitHasError}
					placeholder="Select unit"
					options={unitOptions}
					onChange={(e) =>
						onChangeRow(
							row.id,
							"unitId",
							e.target.value ? Number(e.target.value) : null
						)
					}
					aria-label={`Unit of measurement for row ${rowIndex + 1}`}
					data-testid={`template-row-unit-${row.id}`}
				/>
			</td>
		);
	}

	return (
		<td
			role="gridcell"
			className={cn(CELL_BASE, "bg-gray-50 italic text-tertiary-500")}
			title={cellHint(column.columnType)}
			data-testid={`template-row-${row.id}-placeholder-${column.localId}`}
		>
			{SAMPLE_CELL_LABELS[column.columnType]}
		</td>
	);
};

export default TemplateRowCell;
